import React, { useEffect, useMemo, useRef, useState } from 'react';

const MOBILE_ZOOM = 1.5;
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
import { TEAMS, TEAM_COLOR, TILT_DEG, PERSPECTIVE, BASE, cellFor, tileKey, project, cityBg, cityPlane, shade } from './rtbBoard';
import { RtbTiles, RtbBases } from './RtbTiles';
import { RtbFigure } from './RtbFigure';
import { RtbDice } from './RtbDice';

function fitCell(w, h, tilt, head) {
  let c = 10;
  const th = (tilt * Math.PI) / 180;
  const ext = (t) => {
    const z = 7.5 * t * Math.sin(th);
    return { sb: PERSPECTIVE / (PERSPECTIVE - z), A: 7.5 * t * Math.cos(th) * (PERSPECTIVE / (PERSPECTIVE + z)), B: 7.5 * t * Math.cos(th) * (PERSPECTIVE / (PERSPECTIVE - z)) };
  };
  for (let t = 10; t < 120; t += 0.5) {
    const { sb, A, B } = ext(t);
    if (15 * t * sb > w * 0.99 || A + B + head * t > h) break;
    c = t;
  }
  const { A, B } = ext(c);
  const cy = (h - (A + B + head * c)) / 2 + head * c + A;
  return { c, cy, top: cy - A - head * c, bottom: cy + B };
}

function useSize(ref) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setSize({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

export function RtbBoard({ city, layout, display, myTeam, movable, pendingRoll, turn, dice, canRoll, onRoll, onMove, fx, camera }) {
  const ref = useRef(null);
  const { w, h } = useSize(ref);
  const portrait = h > w * 1.15;
  const tilt = portrait ? 30 : TILT_DEG;
  const big = portrait ? 1.15 : 1;
  const { c: cell, cy, top: bTop, bottom: bBot } = w ? fitCell(w, h, tilt, 1.9 * big) : { c: 0, cy: 0, top: 0, bottom: 0 };
  const P = (r, c) => project(r, c, cell, cx, cy, tilt);
  const S = cell * 15;
  const cx = w / 2;

  const figures = useMemo(() => {
    if (!display || !cell) return [];
    const stacks = {};
    const out = [];
    TEAMS.filter((t) => display[t]).forEach((t) => display[t].forEach((prog, i) => {
      const key = tileKey(t, prog);
      const n = key.endsWith('base') ? 0 : (stacks[key] = (stacks[key] || 0) + 1) - 1;
      const [r, c] = cellFor(t, prog, i);
      const off = [[0, 0], [-0.22, 0.22], [0.22, -0.22], [0.22, 0.22]][n % 4];
      out.push({ t, i, pos: P(r + off[0], c + off[1]) });
    }));
    return out;
  }, [display, cell, cx, cy, tilt]); // eslint-disable-line react-hooks/exhaustive-deps

  const dicePos = cell ? P(7.5, 7.5) : null;
  const fxPos = fx && cell ? P(fx.cell[0], fx.cell[1]) : null;
  const targets = cell && pendingRoll && myTeam && display?.[myTeam] ? movable.map((i) => {
    const p = display[myTeam][i];
    const [r, c] = cellFor(myTeam, p === BASE ? 5 : p + pendingRoll, i);
    return { i, pos: P(r, c) };
  }) : [];
  const diceSize = cell * (portrait ? 3.3 : 2.9);
  const zoom = camera && dicePos ? Math.min(2.6, (h * 0.55) / diceSize) : 1;

  // Mobile camera: zoomed board that follows the action and can be dragged.
  const mz = portrait ? MOBILE_ZOOM : 1;
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const drag = useRef(null);
  const prevFig = useRef({});
  const clampPan = (p) => {
    const rx = Math.max(0, (w * mz - w) / 2);
    const ybase = -((bTop + bBot) / 2 - h / 2) * mz;
    const ry = Math.max(0, ((bBot - bTop) * mz - h) / 2);
    return { x: clamp(p.x, -rx, rx), y: clamp(p.y, ybase - ry, ybase + ry) };
  };
  const centerOn = (pt) => setPan(clampPan({ x: -(pt.x - w / 2) * mz, y: -(pt.y - h / 2) * mz }));
  useEffect(() => {
    if (!portrait) return;
    const moved = figures.find(({ t, i, pos }) => { const p = prevFig.current[`${t}${i}`]; return p && (p.x !== pos.x || p.y !== pos.y); });
    prevFig.current = Object.fromEntries(figures.map(({ t, i, pos }) => [`${t}${i}`, pos]));
    if (moved) centerOn(moved.pos);
  }, [figures]); // eslint-disable-line react-hooks/exhaustive-deps
  const targetKey = targets.map((t) => t.i).join(',');
  useEffect(() => {
    if (!portrait || !targets.length) return;
    const pts = [...targets.map((t) => t.pos), ...figures.filter((f) => f.t === myTeam && movable.includes(f.i)).map((f) => f.pos)];
    centerOn({ x: pts.reduce((a, p) => a + p.x, 0) / pts.length, y: pts.reduce((a, p) => a + p.y, 0) / pts.length });
  }, [targetKey, portrait]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!portrait) setPan({ x: 0, y: 0 }); else setPan((p) => clampPan(p)); }, [portrait, w, h]); // eslint-disable-line react-hooks/exhaustive-deps
  const onDown = (e) => { if (portrait) drag.current = { x: e.clientX, y: e.clientY, p: pan, on: false }; };
  const onDrag = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x, dy = e.clientY - d.y;
    if (!d.on && Math.hypot(dx, dy) < 8) return;
    if (!d.on) { d.on = true; setDragging(true); }
    setPan(clampPan({ x: d.p.x + dx, y: d.p.y + dy }));
  };
  const onUp = () => { drag.current = null; setDragging(false); };
  const camStyle = camera && dicePos
    ? { transform: `scale(${zoom})`, transformOrigin: `${dicePos.x}px ${dicePos.y - diceSize * 0.35}px` }
    : { transform: `translate(${pan.x}px, ${pan.y}px) scale(${mz})`, transformOrigin: '50% 50%' };

  return (
    <div ref={ref} data-testid="rtb-board" data-camera={camera ? 'dice' : 'board'} data-mobile-cam={portrait ? 'on' : 'off'} className={`relative w-full h-full overflow-hidden ${portrait ? 'touch-none' : ''}`}
      onPointerDown={onDown} onPointerMove={onDrag} onPointerUp={onUp} onPointerCancel={onUp} onPointerLeave={onUp}>
      <div className={`absolute inset-0 ${dragging ? '' : 'rtb-cam'}`} style={{ perspective: `${PERSPECTIVE}px`, perspectiveOrigin: `${cx}px ${cy}px`, ...camStyle }}>
      <div data-testid="rtb-city-backdrop" data-city={city} className="absolute inset-0 rtb-backdrop" style={{ backgroundImage: `url('${cityBg(city)}')` }} />
      <div className="absolute inset-0 rtb-backdrop-shade" />
      {cell > 0 && (
        <>
          <div className="absolute rtb-plane" style={{ left: cx - S / 2, top: cy - S / 2, width: S, height: S, transform: `rotateX(${tilt}deg)` }}>
            <div data-testid="rtb-city-plane" className="absolute inset-[-8%] rtb-city" style={{ ...shade(cityPlane(city)), boxShadow: '0 0 60px 20px rgba(0,0,0,0.7)' }} />
            <RtbBases cell={cell} />
            <RtbTiles cell={cell} layout={layout} />
            <div data-testid="rtb-dome-mount" className="rtb-mount absolute rounded-full" style={{ left: 7.5 * cell - diceSize * 0.5, top: 7.5 * cell - diceSize * 0.5, width: diceSize, height: diceSize }} />
          </div>
          <div className="absolute inset-0 pointer-events-none rtb-vignette" />
          <div className="absolute inset-0" style={{ pointerEvents: 'none' }}>
            <div style={{ pointerEvents: 'auto' }}>
              {figures.map(({ t, i, pos }) => (
                <RtbFigure key={`${t}-${i}`} team={t} idx={i} pos={pos} cell={cell} big={big}
                  active={turn === t} movable={t === myTeam && movable.includes(i)} onClick={() => onMove(i)} />
              ))}
              {targets.map(({ i, pos }) => (
                <button type="button" key={`t${i}`} data-testid={`rtb-target-${i}`} onClick={() => onMove(i)} aria-label={`Move crew member ${i + 1} here`} className="rtb-target absolute p-0 bg-transparent" style={{ left: pos.x, top: pos.y, zIndex: Math.round(pos.y) - 1, width: cell * 1.1 * big * pos.s, height: cell * 0.6 * big * pos.s }}>
                  <span className="rtb-display" style={{ fontSize: Math.max(11, cell * 0.36 * big) }}>+{pendingRoll}</span>
                </button>
              ))}
              <RtbDice x={dicePos.x} y={dicePos.y} size={diceSize} value={dice.value} rolling={dice.rolling} team={dice.team || turn} teamColor={TEAM_COLOR[dice.team || turn]} canRoll={canRoll} onRoll={onRoll} />
            </div>
            {fxPos && <div key={fx.key} className="rtb-burst absolute" style={{ left: fxPos.x, top: fxPos.y, zIndex: 99999, '--c': '#F87171' }} />}
          </div>
        </>
      )}
      </div>
    </div>
  );
}
